import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CacheSettings, hypecastHttpContext } from '@app/core/http/http-context';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { S2T, S2TText, TranscriptCue, TranscriptInfo, TranscriptionState } from '../models/classes/Transcript';
import { Response } from './local/Response';

export interface TranscriptMeta {
  id: string;
  state: TranscriptionState;
  language: string;
  isOriginal: boolean;
  fileId: string | null;
  url: string | null;
}

/** Resolves the storage URL of a transcript; the default reads `TranscriptInfo.url`. */
export type TranscriptUrlResolver = (transcript: TranscriptInfo) => Observable<string | null>;

@Injectable()
export class TranscriptService {
  constructor(private httpClient: HttpClient) {}

  getOriginal(transcripts: TranscriptInfo[] | undefined): TranscriptInfo | null {
    if (!transcripts || transcripts.length === 0) {
      return null;
    }
    return transcripts.find((t) => t.isOriginal && t.state === TranscriptionState.Completed) ?? null;
  }

  getBestForLocale(transcripts: TranscriptInfo[] | undefined, locale: string | null | undefined): TranscriptInfo | null {
    if (!transcripts?.length) {
      return null;
    }

    const completed = transcripts.filter((transcript) => transcript.state === TranscriptionState.Completed);
    if (!completed.length) {
      return null;
    }

    const normalizedLocale = this.normalizeLocale(locale);
    if (normalizedLocale) {
      const exact = completed.find(
        (transcript) => this.normalizeLocale(transcript.language)?.toLowerCase() === normalizedLocale.toLowerCase(),
      );
      if (exact) {
        return exact;
      }

      const baseLanguage = normalizedLocale.split('-')[0].toLowerCase();
      const regional = completed.find(
        (transcript) => this.normalizeLocale(transcript.language)?.split('-')[0].toLowerCase() === baseLanguage,
      );
      if (regional) {
        return regional;
      }
    }

    return completed.find((transcript) => transcript.isOriginal) ?? null;
  }

  buildCues(s2t: S2T | null): TranscriptCue[] {
    if (!s2t?.text?.length) {
      return [];
    }
    const cues: TranscriptCue[] = [];
    for (const utterance of s2t.text) {
      if (!utterance.words?.length) {
        continue;
      }
      let sentence: typeof utterance.words = [];
      for (const word of utterance.words) {
        sentence.push(word);
        if (/[.!?…]["')\]]?$/.test(word.word)) {
          cues.push(this.cueFromWords(utterance.speaker, sentence));
          sentence = [];
        }
      }
      if (sentence.length) {
        cues.push(this.cueFromWords(utterance.speaker, sentence));
      }
    }
    return cues;
  }

  /**
   * Loads the cues for `transcript`. Translated transcripts written before the worker learned
   * the S2T word timing carry `time 0` on every word, which makes highlighting and click-to-seek
   * impossible; for those the original transcript (same episode, segments correspond index by
   * index) is loaded as well and its segment spans are copied over.
   */
  loadCues(
    transcript: TranscriptInfo,
    transcripts: TranscriptInfo[] | undefined,
    resolveUrl: TranscriptUrlResolver = (t) => of(t.url ?? null),
  ): Observable<TranscriptCue[]> {
    return resolveUrl(transcript).pipe(
      switchMap((url) => (url ? this.loadS2T(url) : of(null))),
      switchMap((s2t) => {
        if (!s2t || transcript.isOriginal || this.hasTimings(s2t)) {
          return of(s2t);
        }
        const original = this.getOriginal(transcripts);
        if (!original || original.id === transcript.id) {
          return of(s2t);
        }
        return resolveUrl(original).pipe(
          switchMap((originalUrl) => (originalUrl ? this.loadS2T(originalUrl) : of(null))),
          map((originalS2T) => (originalS2T ? this.alignTimings(s2t, originalS2T) : s2t)),
        );
      }),
      map((s2t) => this.buildCues(s2t)),
    );
  }

  /** True when at least one word carries a usable position. */
  hasTimings(s2t: S2T | null): boolean {
    return !!s2t?.text?.some((utterance) => utterance.words?.some((word) => word.time > 0 || word.duration > 0));
  }

  /**
   * Copies the span of every original utterance onto the utterance with the same index in the
   * translation and spreads the translated words evenly over it (the translation has no
   * per-word alignment to the audio; even spacing is what the worker does for new files too).
   */
  alignTimings(translated: S2T, original: S2T): S2T {
    const text = translated.text.map((utterance, index): S2TText => {
      const source = original.text[index];
      const sourceWords = source?.words?.filter((word) => word.time > 0 || word.duration > 0) ?? [];
      if (!sourceWords.length || !utterance.words?.length) {
        return utterance;
      }
      const start = sourceWords[0].time;
      const last = sourceWords[sourceWords.length - 1];
      const span = Math.max(0, last.time + last.duration - start);
      const count = utterance.words.length;
      return {
        ...utterance,
        words: utterance.words.map((word, wordIndex) => {
          const wordStart = Math.round(start + (span * wordIndex) / count);
          const wordEnd = Math.round(start + (span * (wordIndex + 1)) / count);
          return { ...word, time: wordStart, duration: Math.max(0, wordEnd - wordStart) };
        }),
      };
    });
    return { ...translated, text };
  }

  private cueFromWords(speaker: string, words: S2T['text'][number]['words']): TranscriptCue {
    const first = words[0];
    const last = words[words.length - 1];
    return {
      speaker,
      text: words.map((w) => w.word).join(' '),
      startMs: first.time,
      endMs: last.time + last.duration,
    };
  }

  getTranscriptMeta(episodeId: string, transcriptId: string): Observable<TranscriptMeta> {
    const cache = Object.assign(new CacheSettings(), { expires: 60 * 60 });
    return this.httpClient.get<Response<TranscriptMeta>>(
      `episodes/${episodeId}/transcripts/${transcriptId}`,
      {context: hypecastHttpContext({cache})},
    ).pipe(map((r) => r.data));
  }

  loadS2T(url: string): Observable<S2T> {
    return this.httpClient.get<unknown>(
      url,
      {context: hypecastHttpContext({disableAccessToken: true})},
    ).pipe(map((raw) => normalizeS2T(raw)));
  }

  private normalizeLocale(value: string | null | undefined): string | null {
    const candidate = value?.trim().replace(/_/g, '-');
    if (!candidate) {
      return null;
    }
    try {
      return Intl.getCanonicalLocales(candidate)[0] ?? null;
    } catch {
      return null;
    }
  }
}

function normalizeS2T(raw: any): S2T {
  const pick = (obj: any, ...keys: string[]) => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined) return obj[k];
    }
    return undefined;
  };
  const head = pick(raw, 'head', 'Head') ?? {};
  const speakers = pick(raw, 'speakers', 'Speakers') ?? [];
  const text = pick(raw, 'text', 'Text') ?? [];
  // Two word shapes exist in storage: S2T `time`/`duration` (ms) and the segment shape's
  // `start`/`end` (seconds). Read either into ms.
  const wordTime = (w: any): number => {
    const time = pick(w, 'time', 'Time');
    if (time !== undefined && time !== null) return time;
    const start = pick(w, 'start', 'Start');
    return typeof start === 'number' ? Math.round(start * 1000) : 0;
  };
  const wordDuration = (w: any): number => {
    const duration = pick(w, 'duration', 'Duration');
    if (duration !== undefined && duration !== null) return duration;
    const start = pick(w, 'start', 'Start');
    const end = pick(w, 'end', 'End');
    return typeof start === 'number' && typeof end === 'number' ? Math.max(0, Math.round((end - start) * 1000)) : 0;
  };
  return {
    version: pick(raw, 'version', 'Version') ?? '',
    head: {
      duration: pick(head, 'duration', 'Duration') ?? 0,
      language: pick(head, 'language', 'Language') ?? '',
    },
    speakers: speakers.map((s: any) => ({ name: pick(s, 'name', 'Name') ?? '' })),
    text: text.map((u: any) => ({
      speaker: pick(u, 'speaker', 'Speaker') ?? '',
      words: (pick(u, 'words', 'Words') ?? []).map((w: any) => ({
        word: pick(w, 'word', 'Word') ?? '',
        time: wordTime(w),
        duration: wordDuration(w),
        confidence: pick(w, 'confidence', 'Confidence') ?? 0,
      })),
    })),
  };
}
